import { ActivityFeedContainer } from '@/activity-feed/components/activity-feed-container';
import { SummarizedComplaints } from '@/complaints/components-property-details-view/summarized-complaints';
import { NotFoundPage } from '@/components/NotFoundPage';
import { PROPERTY_DETAILS_TAB_NAMES as navItems } from '@/constants';
import { PropertyDetailsErrorBoundary } from '@/error-boundaries';
import { LicenseContent } from '@/license/components/license-content';
import { PropertyHistoryContent } from '@/property-details/components/property-history/property-history-content';
import { InspectionsContent } from '@/property-inspections';
import { PropertyTaxReportingView } from '@/property-tax-reporting-tab/property-tax-reporting-view';
import { DashboardRouteParams } from '@/types';
import { useActivityFeed } from '@/utils/hooks/data/useActivityFeed';
import { useComplaints } from '@/utils/hooks/data/useComplaints';
import { useInspections } from '@/utils/hooks/data/useInspections';
import { useLicenses } from '@/utils/hooks/data/useLicenses';
import { usePropertyTaxes } from '@/utils/hooks/data/usePropertyTaxes';
import { useViolations } from '@/utils/hooks/data/useViolations';
import { PrintWrapper } from '@/utils/print-wrapper';
import { ViolationsTab } from '@/violations-tab/violations-tab';
import { CleanModal } from '@/widgets/components/clean-modal';
import { Redirect, Route, Switch, useParams } from 'react-router-dom';
import { PropertyDetailsContent } from './property-details-content';
import { HeaderContainer } from './property-details-tabs/header-container';

const PROPERTY_DETAILS_ROUTE = '/:region/:tab/:propertyDeckardId';

/**
 * Modal container for displaying property details data
 */
function PropertyDetailsPageComponent() {
  const { propertyDeckardId, region, tab } = useParams<DashboardRouteParams>();
  const { isEnabled: isLicenseEnabled } = useLicenses();
  const { isEnabled: isComplaintsEnabled } = useComplaints();
  const { isEnabled: isActivityFeedEnabled } = useActivityFeed();
  const { isEnabled: isPropertyTaxesEnabled } = usePropertyTaxes();
  const { isEnabled: isViolationsEnabled } = useViolations();
  const { isEnabled: isInspectionsEnabled } = useInspections();

  return (
    // Rendering of property details is being controlled from the parent component
    // So setting active to true to ensure the modal is always rendered when this component is rendered
    <CleanModal active>
      <PrintWrapper>
        <HeaderContainer />
        <Switch>
          {/* Redirect to the overview tab if no tab is provided in the url */}
          <Route
            exact
            path={PROPERTY_DETAILS_ROUTE}
            component={() => (
              <Redirect
                to={`/${region}/${tab}/${propertyDeckardId}/${navItems.overview}`}
              />
            )}
          />
          <Route
            exact
            path={`${PROPERTY_DETAILS_ROUTE}/${navItems.overview}`}
            component={PropertyDetailsContent}
          />
          {isLicenseEnabled && (
            <Route
              path={`${PROPERTY_DETAILS_ROUTE}/${navItems.license}`}
              component={LicenseContent}
            />
          )}
          {isActivityFeedEnabled && (
            <Route
              path={`${PROPERTY_DETAILS_ROUTE}/${navItems.activity}`}
              component={ActivityFeedContainer}
            />
          )}
          {isComplaintsEnabled && (
            <Route
              path={`${PROPERTY_DETAILS_ROUTE}/${navItems.complaints}`}
              component={SummarizedComplaints}
            />
          )}
          <Route
            path={`${PROPERTY_DETAILS_ROUTE}/${navItems.history}`}
            component={PropertyHistoryContent}
          />
          {isInspectionsEnabled && (
            <Route
              path={`${PROPERTY_DETAILS_ROUTE}/${navItems.inspections}`}
              component={InspectionsContent}
            />
          )}
          {isPropertyTaxesEnabled && (
            <Route
              path={`${PROPERTY_DETAILS_ROUTE}/${navItems.propertyOverviewTaxTab}`}
              component={PropertyTaxReportingView}
            />
          )}
          {isViolationsEnabled && (
            <Route
              path={`${PROPERTY_DETAILS_ROUTE}/${navItems.violations}`}
              component={ViolationsTab}
            />
          )}

          <Route path="*" component={NotFoundPage} />
        </Switch>
      </PrintWrapper>
    </CleanModal>
  );
}

export const PropertyDetailsPage = PropertyDetailsErrorBoundary.withBoundary()(
  PropertyDetailsPageComponent
);
